import { Col, Input, Row, Select, DatePicker } from "antd";
import { useFormik } from "formik";
import { cloneDeep, get, toLower, toNumber } from "lodash";
import Button from "../../../../../components/functional_components/Button/PFButton";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../../../../redux/ducks/Loading";
import { showError, showSuccess, trimData } from "../../../../../utils/utilities";
import { SaveOutlined, CloseOutlined } from "@ant-design/icons";
import { dbDate, langMonth } from "../../../../../utils/properties";
import RiskOpportunities from "../../../../../services/api/RisksOpportunities";
import { resetData } from "../../../../../redux/ducks/RiskAnalysis";
import dayjs from "dayjs";

export default function RiskAnalysisDetail() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langMonth[toLower(currentLanguage)];
  const riskAnalysisData = useSelector((state) => state.RiskAnalysisDuck);
  const { Option } = Select;
  const [state, setState] = useState({
    data: [],
    loading: false,
  });

  const type = [
    {
      value: t("projectsManagement.riskAnalysis.types.costs"),
      id: "COSTS",
    },
    {
      value: t("projectsManagement.riskAnalysis.types.contractSow"),
      id: "CONTRACT_SOW",
    },
    {
      value: t("projectsManagement.riskAnalysis.types.customerRelations"),
      id: "CUSTOMER_RELATIONS",
    },
    {
      value: t("projectsManagement.riskAnalysis.types.team"),
      id: "TEAM",
    },
    {
      value: t("projectsManagement.riskAnalysis.types.operationalDelivery"),
      id: "OPERATIONAL_DELIVERY",
    },
    {
      value: t("projectsManagement.riskAnalysis.types.other"),
      id: "OTHER",
    },
  ];

  const levels = [
    {
      value: 1,
      id: "1",
    },
    {
      value: 2,
      id: "2",
    },
    {
      value: 3,
      id: "3",
    },
  ];

  const probabilities = [
    {
      value: 1,
      id: "1",
    },
    {
      value: 2,
      id: "2",
    },
    {
      value: 3,
      id: "3",
    },
  ];

  const status =
    riskAnalysisData.panel === "risks"
      ? [
          {
            value: t("projectsManagement.riskAnalysis.status.toBeEvaluated"),
            id: "TO_BE_EVALUATED",
          },
          {
            value: t("projectsManagement.riskAnalysis.status.evaluating"),
            id: "EVALUATING",
          },
          {
            value: t("projectsManagement.riskAnalysis.status.addressed"),
            id: "ADDRESSED",
          },
          {
            value: t("projectsManagement.riskAnalysis.status.absorber"),
            id: "ABSORBER",
          },
        ]
      : [
          {
            value: t("projectsManagement.riskAnalysis.status.toBeEvaluated"),
            id: "TO_BE_EVALUATED",
          },
          {
            value: t("projectsManagement.riskAnalysis.status.evaluating"),
            id: "EVALUATING",
          },
          {
            value: t("projectsManagement.riskAnalysis.status.gained"),
            id: "GAINED",
          },
          {
            value: t("projectsManagement.riskAnalysis.status.aborted"),
            id: "ABORTED",
          },
        ];

  useEffect(() => {
    if (!riskAnalysisData.new) loadRiskAnalysis();
  }, []);

  const loadRiskAnalysis = async () => {
    dispatch(setLoading(true));
    const services =
      riskAnalysisData.panel === "opportunities"
        ? await RiskOpportunities.opportunityDetail(riskAnalysisData.data.id, riskAnalysisData.data.projectId)
        : await RiskOpportunities.riskDetail(riskAnalysisData.data.id, riskAnalysisData.data.projectId);
    if (!services.error && services.data[0]) {
      setState((prevState) => ({ ...prevState, data: get(services, "data[0]", []) }));
    } else showError(get(services, "error.messageCode", "ER000"));
    dispatch(setLoading(false));
  };

  const formikRiskAnalysis = useFormik({
    initialValues: {
      type: get(state.data, "type", null),
      description: get(state.data, "description", ""),
      impactDescription: get(state.data, "impactDescription", ""),
      impactEvaluationInitial: get(state.data, "impactEvaluationInitial", ""),
      impactEvaluationCurrent: get(state.data, "impactEvaluationCurrent", ""),
      impactLevel: get(state.data, "impactLevel", null),
      probably: get(state.data, "probably", null),
      actionPlan: get(state.data, "actionPlan", ""),
      actionOwner: get(state.data, "actionOwner", ""),
      dueDate: get(state.data, "dueDate", null) !== null ? dayjs(state.data.dueDate, dbDate).format(dateFormat) : null,
      status: get(state.data, "status", null),
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.type) errors.type = "Required!";
      if (!values.description) errors.description = "Required!";
      if (!values.impactDescription) errors.impactDescription = "Required!";
      if (!values.impactEvaluationInitial) errors.impactEvaluationInitial = "Required!";
      if (!values.impactEvaluationCurrent) errors.impactEvaluationCurrentid = "Required!";
      if (!values.impactLevel) errors.impactLevel = "Required!";
      if (!values.probably) errors.probably = "Required!";
      if (!values.actionPlan) errors.actionPlan = "Required!";
      if (!values.actionOwner) errors.actionOwner = "Required!";
      if (!values.dueDate) errors.dueDate = "Required!";
      if (!values.status) errors.status = "Required!";
      return errors;
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      let data = cloneDeep(values);
      data.id = riskAnalysisData.data.projectId;
      data.projectId = riskAnalysisData.data.id;
      data.impactEvaluationInitial = toNumber(data.impactEvaluationInitial);
      data.impactEvaluationCurrent = toNumber(data.impactEvaluationCurrent);
      data.impactLevel = toNumber(data.impactLevel);
      data.probably = toNumber(data.probably);
      data.dueDate = dayjs(data.dueDate, dateFormat).format(dbDate);
      data.riskOrOpportunity = riskAnalysisData.panel === "risks" ? "RISK" : "OPPORTUNITY";
      saveRiskAnalysis(data);
    },
  });

  const saveRiskAnalysis = async (data) => {
    dispatch(setLoading(true));
    const services = await RiskOpportunities.save(data);
    if (!services.error) {
      showSuccess();
      dispatch(resetData());
    } else {
      showError(get(services, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  return (
    <>
      <Row gutter={[48, 80]}>
        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="type">
              {t("projectsManagement.riskAnalysis.table.type")}*
            </label>
          </Row>
          <Row>
            <Select
              showSearch
              placeholder={t("projectsManagement.riskAnalysis.table.type")}
              id="type"
              name="type"
              className="w-100"
              onBlur={formikRiskAnalysis.handleBlur("type")}
              onChange={formikRiskAnalysis.handleChange("type")}
              value={formikRiskAnalysis.values.type}
            >
              {type.map((item) => (
                <Option key={item.id} value={item.id} name={item.value}>
                  {item.value}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="description">
              {t("projectsManagement.riskAnalysis.table.riskDescription")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="description"
              name="description"
              placeholder={t("projectsManagement.riskAnalysis.table.riskDescription")}
              onChange={formikRiskAnalysis.handleChange("description")}
              onBlur={formikRiskAnalysis.handleBlur("description")}
              value={formikRiskAnalysis.values.description}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="impactDescription">
              {t("projectsManagement.riskAnalysis.table.impactDescription")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="impactDescription"
              name="impactDescription"
              placeholder={t("projectsManagement.riskAnalysis.table.impactDescription")}
              onChange={formikRiskAnalysis.handleChange("impactDescription")}
              onBlur={formikRiskAnalysis.handleBlur("impactDescription")}
              value={formikRiskAnalysis.values.impactDescription}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="impactEvaluationInitial">
              {t("projectsManagement.riskAnalysis.table.impactEvaluationInitial")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="impactEvaluationInitial"
              name="impactEvaluationInitial"
              placeholder={t("projectsManagement.riskAnalysis.table.impactEvaluationInitial")}
              onChange={formikRiskAnalysis.handleChange("impactEvaluationInitial")}
              onBlur={formikRiskAnalysis.handleBlur("impactEvaluationInitial")}
              value={formikRiskAnalysis.values.impactEvaluationInitial}
              type="number"
              min={0}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="impactEvaluationCurrent">
              {t("projectsManagement.riskAnalysis.table.impactEvaluationCurrent")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="impactEvaluationCurrent"
              name="impactEvaluationCurrent"
              placeholder={t("projectsManagement.riskAnalysis.table.impactEvaluationCurrent")}
              onChange={formikRiskAnalysis.handleChange("impactEvaluationCurrent")}
              onBlur={formikRiskAnalysis.handleBlur("impactEvaluationCurrent")}
              value={formikRiskAnalysis.values.impactEvaluationCurrent}
              type="number"
              min={0}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <Col span={10}>
              <Row>
                <label className="fw-bold mt-10 mb-10" htmlFor="impactLevel">
                  {t("projectsManagement.riskAnalysis.table.impactLevel")}*
                </label>
              </Row>
              <Row>
                <Select
                  showSearch
                  placeholder={t("projectsManagement.riskAnalysis.table.impactLevel")}
                  id="impactLevel"
                  name="impactLevel"
                  className="w-100"
                  onBlur={formikRiskAnalysis.handleBlur("impactLevel")}
                  onChange={formikRiskAnalysis.handleChange("impactLevel")}
                  value={formikRiskAnalysis.values.impactLevel}
                >
                  {levels.map((item) => (
                    <Option key={item.id} value={item.id} name={item.value}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </Row>
            </Col>
            <Col offset={2} span={10}>
              <Row>
                <label className="fw-bold mt-10 mb-10" htmlFor="probably">
                  {t("projectsManagement.riskAnalysis.table.probability")}*
                </label>
              </Row>
              <Row>
                <Select
                  showSearch
                  placeholder={t("projectsManagement.riskAnalysis.table.probability")}
                  id="probably"
                  name="probably"
                  className="w-100"
                  onBlur={formikRiskAnalysis.handleBlur("probably")}
                  onChange={formikRiskAnalysis.handleChange("probably")}
                  value={formikRiskAnalysis.values.probably}
                >
                  {probabilities.map((item) => (
                    <Option key={item.id} value={item.id} name={item.value}>
                      {item.value}
                    </Option>
                  ))}
                </Select>
              </Row>
            </Col>
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="actionPlan">
              {t("projectsManagement.riskAnalysis.table.actionPlan")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="actionPlan"
              name="actionPlan"
              placeholder={t("projectsManagement.riskAnalysis.table.actionPlan")}
              onChange={formikRiskAnalysis.handleChange("actionPlan")}
              onBlur={formikRiskAnalysis.handleBlur("actionPlan")}
              value={formikRiskAnalysis.values.actionPlan}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="actionOwner">
              {t("projectsManagement.riskAnalysis.table.actionOwner")}*
            </label>
          </Row>
          <Row>
            <Input
              required
              id="actionOwner"
              name="actionOwner"
              placeholder={t("projectsManagement.riskAnalysis.table.actionOwner")}
              onChange={formikRiskAnalysis.handleChange("actionOwner")}
              onBlur={formikRiskAnalysis.handleBlur("actionOwner")}
              value={formikRiskAnalysis.values.actionOwner}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="dueDate">
              {t("projectsManagement.riskAnalysis.table.dueDate")}*
            </label>
          </Row>
          <Row>
            <DatePicker
              id="dueDate"
              name="dueDate"
              placeholder={t("projectsManagement.riskAnalysis.table.selectMonth")}
              format={dateFormat}
              picker="month"
              value={
                formikRiskAnalysis.values.dueDate ? dayjs(formikRiskAnalysis.values.dueDate, dateFormat) : undefined
              }
              onChange={(_, dateString) => {
                formikRiskAnalysis.setFieldValue("dueDate", dateString);
              }}
            />
          </Row>
        </Col>

        <Col lg={8} md={8} xs={24} sm={10}>
          <Row>
            <label className="fw-bold mt-10 mb-10" htmlFor="status">
              {t("projectsManagement.riskAnalysis.table.status")}*
            </label>
          </Row>
          <Row>
            <Select
              showSearch
              placeholder={t("projectsManagement.riskAnalysis.table.status")}
              id="status"
              name="status"
              className="w-100"
              onBlur={formikRiskAnalysis.handleBlur("status")}
              onChange={formikRiskAnalysis.handleChange("status")}
              value={formikRiskAnalysis.values.status}
            >
              {status.map((item) => (
                <Option key={item.id} value={item.id} name={item.value}>
                  {item.value}
                </Option>
              ))}
            </Select>
          </Row>
        </Col>
      </Row>
      <Row justify="end" className="mb-1em mt-10">
        <Button
          currentIcon={<CloseOutlined className="icon-medium" />}
          type="secondary"
          clickCallback={() => dispatch(resetData())}
          isLoading={state.loading}
          className="mr-10"
        />
        <Button
          currentIcon={<SaveOutlined className="icon-medium" />}
          clickCallback={formikRiskAnalysis.submitForm}
          isDisabled={!(formikRiskAnalysis.dirty && formikRiskAnalysis.isValid)}
          isLoading={state.loading}
        />
      </Row>
    </>
  );
}

// Selezionando la lingua italiano da invalid date
// Quali type ci sono e quali status
